/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Tessera Backend API
 * Tessera backend application with Bearer token authentication and multi-tenant support
 */
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  InvalidateOptions,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { ErrorType } from '../api-instance';
import { apiInstance } from '../api-instance';
import type {
  GetUserListParams,
  GetUserRolesPathParameters,
  GetUsersByRoleParams,
  GetUsersByRolePathParameters,
  ListUserDirectoryParams,
  TLHTTPValidationError,
  TLRolesListResponse,
  TLUserDirectoryResponse,
  TLUserListResponse,
} from './tessera-backend-api.schemas';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @deprecated
 * @summary Get User List
 */
export const getUserList = (
  params?: GetUserListParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLUserListResponse>({ url: `/users`, method: 'GET', params, signal }, options);
};

export const getGetUserListQueryKey = (params?: GetUserListParams) => {
  return ['users', ...(params ? [params] : [])] as const;
};

export const getGetUserListQueryOptions = <
  TData = Awaited<ReturnType<typeof getUserList>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetUserListParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getUserList>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetUserListQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getUserList>>> = ({ signal }) =>
    getUserList(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getUserList>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetUserListQueryResult = NonNullable<Awaited<ReturnType<typeof getUserList>>>;
export type GetUserListQueryError = ErrorType<TLHTTPValidationError>;

export function useGetUserList<
  TData = Awaited<ReturnType<typeof getUserList>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: undefined | GetUserListParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getUserList>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getUserList>>,
          TError,
          Awaited<ReturnType<typeof getUserList>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetUserList<
  TData = Awaited<ReturnType<typeof getUserList>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetUserListParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getUserList>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getUserList>>,
          TError,
          Awaited<ReturnType<typeof getUserList>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetUserList<
  TData = Awaited<ReturnType<typeof getUserList>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetUserListParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getUserList>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @deprecated
 * @summary Get User List
 */

export function useGetUserList<
  TData = Awaited<ReturnType<typeof getUserList>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetUserListParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getUserList>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetUserListQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @deprecated
 * @summary Get User List
 */
export const prefetchGetUserListQuery = async <
  TData = Awaited<ReturnType<typeof getUserList>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  params?: GetUserListParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getUserList>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetUserListQueryOptions(params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @deprecated
 * @summary Get User List
 */
export const invalidateGetUserList = async (
  queryClient: QueryClient,
  params?: GetUserListParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetUserListQueryKey(params) }, options);

  return queryClient;
};

/**
 * @summary Get Users By Role
 */
export const getUsersByRole = (
  { roleName }: GetUsersByRolePathParameters,
  params?: GetUsersByRoleParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLUserListResponse>({ url: `/users/by-role/${roleName}`, method: 'GET', params, signal }, options);
};

export const getGetUsersByRoleQueryKey = (
  { roleName }: GetUsersByRolePathParameters,
  params?: GetUsersByRoleParams,
) => {
  return ['users', 'by-role', roleName, ...(params ? [params] : [])] as const;
};

export const getGetUsersByRoleQueryOptions = <
  TData = Awaited<ReturnType<typeof getUsersByRole>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { roleName }: GetUsersByRolePathParameters,
  params?: GetUsersByRoleParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getUsersByRole>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetUsersByRoleQueryKey({ roleName }, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getUsersByRole>>> = ({ signal }) =>
    getUsersByRole({ roleName }, params, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: roleName !== null && roleName !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getUsersByRole>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetUsersByRoleQueryResult = NonNullable<Awaited<ReturnType<typeof getUsersByRole>>>;
export type GetUsersByRoleQueryError = ErrorType<TLHTTPValidationError>;

export function useGetUsersByRole<
  TData = Awaited<ReturnType<typeof getUsersByRole>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetUsersByRolePathParameters,
  params: undefined | GetUsersByRoleParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getUsersByRole>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getUsersByRole>>,
          TError,
          Awaited<ReturnType<typeof getUsersByRole>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetUsersByRole<
  TData = Awaited<ReturnType<typeof getUsersByRole>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetUsersByRolePathParameters,
  params?: GetUsersByRoleParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getUsersByRole>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getUsersByRole>>,
          TError,
          Awaited<ReturnType<typeof getUsersByRole>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetUsersByRole<
  TData = Awaited<ReturnType<typeof getUsersByRole>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetUsersByRolePathParameters,
  params?: GetUsersByRoleParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getUsersByRole>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Users By Role
 */

export function useGetUsersByRole<
  TData = Awaited<ReturnType<typeof getUsersByRole>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { roleName }: GetUsersByRolePathParameters,
  params?: GetUsersByRoleParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getUsersByRole>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetUsersByRoleQueryOptions({ roleName }, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Users By Role
 */
export const prefetchGetUsersByRoleQuery = async <
  TData = Awaited<ReturnType<typeof getUsersByRole>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { roleName }: GetUsersByRolePathParameters,
  params?: GetUsersByRoleParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getUsersByRole>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetUsersByRoleQueryOptions({ roleName }, params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Users By Role
 */
export const invalidateGetUsersByRole = async (
  queryClient: QueryClient,
  { roleName }: GetUsersByRolePathParameters,
  params?: GetUsersByRoleParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetUsersByRoleQueryKey({ roleName }, params) }, options);

  return queryClient;
};

/**
 * @summary List User Directory
 */
export const listUserDirectory = (
  params?: ListUserDirectoryParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLUserDirectoryResponse>({ url: `/users/directory`, method: 'GET', params, signal }, options);
};

export const getListUserDirectoryQueryKey = (params?: ListUserDirectoryParams) => {
  return ['users', 'directory', ...(params ? [params] : [])] as const;
};

export const getListUserDirectoryQueryOptions = <
  TData = Awaited<ReturnType<typeof listUserDirectory>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListUserDirectoryParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listUserDirectory>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListUserDirectoryQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listUserDirectory>>> = ({ signal }) =>
    listUserDirectory(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listUserDirectory>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListUserDirectoryQueryResult = NonNullable<Awaited<ReturnType<typeof listUserDirectory>>>;
export type ListUserDirectoryQueryError = ErrorType<TLHTTPValidationError>;

export function useListUserDirectory<
  TData = Awaited<ReturnType<typeof listUserDirectory>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: undefined | ListUserDirectoryParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof listUserDirectory>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listUserDirectory>>,
          TError,
          Awaited<ReturnType<typeof listUserDirectory>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListUserDirectory<
  TData = Awaited<ReturnType<typeof listUserDirectory>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListUserDirectoryParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listUserDirectory>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listUserDirectory>>,
          TError,
          Awaited<ReturnType<typeof listUserDirectory>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListUserDirectory<
  TData = Awaited<ReturnType<typeof listUserDirectory>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListUserDirectoryParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listUserDirectory>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List User Directory
 */

export function useListUserDirectory<
  TData = Awaited<ReturnType<typeof listUserDirectory>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListUserDirectoryParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listUserDirectory>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListUserDirectoryQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary List User Directory
 */
export const prefetchListUserDirectoryQuery = async <
  TData = Awaited<ReturnType<typeof listUserDirectory>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  params?: ListUserDirectoryParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listUserDirectory>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getListUserDirectoryQueryOptions(params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary List User Directory
 */
export const invalidateListUserDirectory = async (
  queryClient: QueryClient,
  params?: ListUserDirectoryParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getListUserDirectoryQueryKey(params) }, options);

  return queryClient;
};

/**
 * Get all roles assigned to a user by email.
 * @summary Get User Roles
 */
export const getUserRoles = (
  { email }: GetUserRolesPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLRolesListResponse>({ url: `/users/${email}/roles`, method: 'GET', signal }, options);
};

export const getGetUserRolesQueryKey = ({ email }: GetUserRolesPathParameters) => {
  return ['users', email, 'roles'] as const;
};

export const getGetUserRolesQueryOptions = <
  TData = Awaited<ReturnType<typeof getUserRoles>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { email }: GetUserRolesPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getUserRoles>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetUserRolesQueryKey({ email });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getUserRoles>>> = ({ signal }) =>
    getUserRoles({ email }, requestOptions, signal);

  return { queryKey, queryFn, enabled: email !== null && email !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getUserRoles>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetUserRolesQueryResult = NonNullable<Awaited<ReturnType<typeof getUserRoles>>>;
export type GetUserRolesQueryError = ErrorType<TLHTTPValidationError>;

export function useGetUserRoles<
  TData = Awaited<ReturnType<typeof getUserRoles>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetUserRolesPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getUserRoles>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getUserRoles>>,
          TError,
          Awaited<ReturnType<typeof getUserRoles>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetUserRoles<
  TData = Awaited<ReturnType<typeof getUserRoles>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetUserRolesPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getUserRoles>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getUserRoles>>,
          TError,
          Awaited<ReturnType<typeof getUserRoles>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetUserRoles<
  TData = Awaited<ReturnType<typeof getUserRoles>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetUserRolesPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getUserRoles>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get User Roles
 */

export function useGetUserRoles<
  TData = Awaited<ReturnType<typeof getUserRoles>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { email }: GetUserRolesPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getUserRoles>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetUserRolesQueryOptions({ email }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get User Roles
 */
export const prefetchGetUserRolesQuery = async <
  TData = Awaited<ReturnType<typeof getUserRoles>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { email }: GetUserRolesPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getUserRoles>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetUserRolesQueryOptions({ email }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get User Roles
 */
export const invalidateGetUserRoles = async (
  queryClient: QueryClient,
  { email }: GetUserRolesPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetUserRolesQueryKey({ email }) }, options);

  return queryClient;
};
